import React, { useRef, useState } from "react";
import { useGarmentMeasurementServiceContext } from "../../../contexts/GarmentMeasurementServiceContext";
import { processImageForDisplay } from "../../../utilities/helper/processImageForDisplay";
import { GarmentMeasurementDetail } from "../../../utilities/types/free-trial/garment-measurement-service/types";

const isHorizontalMeasurement = (detail: GarmentMeasurementDetail): boolean => {
  if (!detail?.Points) return true;

  const xDiff = Math.abs(detail.Points.Point2.X - detail.Points.Point1.X);
  const yDiff = Math.abs(detail.Points.Point2.Y - detail.Points.Point1.Y);

  return xDiff > yDiff;
};

const hasValidCoordinates = (detail: GarmentMeasurementDetail): boolean => {
  if (!detail?.Points) return false;

  const { Point1, Point2 } = detail.Points;

  return !((Point1.X === 0 && Point2.X === 0 && Point1.Y === 0 && Point2.Y === 0) || Point1.X === undefined || Point2.X === undefined || Point1.Y === undefined || Point2.Y === undefined);
};

const InteractiveGarmentMeasurementResult: React.FC = () => {
  const { garmentMeasurementResult, uploadedImages, currentImageIndex, getCurrentMeasurementError } = useGarmentMeasurementServiceContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  const validImages = uploadedImages.filter((img) => img !== "");
  const measurementError = getCurrentMeasurementError?.();

  if (validImages.length === 0) {
    return null;
  }

  const safeIndex = Math.min(currentImageIndex, validImages.length - 1);
  const currentImage = validImages[safeIndex];
  const imageBase64 = processImageForDisplay(currentImage);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageDimensions({
      width: img.naturalWidth || 1200,
      height: img.naturalHeight || 1200,
    });
  };

  const getMeasurementLines = () => {
    if (measurementError || !garmentMeasurementResult?.Details) return null;

    const { Details } = garmentMeasurementResult;
    const lines = [];

    const containerWidth = containerRef.current?.offsetWidth || 240;
    const containerHeight = containerRef.current?.offsetHeight || 400;

    const imageAspectRatio = imageDimensions.width / imageDimensions.height;
    let displayedImageWidth, displayedImageHeight;

    if (containerWidth / containerHeight > imageAspectRatio) {
      displayedImageHeight = containerHeight;
      displayedImageWidth = containerHeight * imageAspectRatio;
    } else {
      displayedImageWidth = containerWidth;
      displayedImageHeight = containerWidth / imageAspectRatio;
    }

    const offsetX = (containerWidth - displayedImageWidth) / 2;
    const offsetY = (containerHeight - displayedImageHeight) / 2;

    for (const [key, detail] of Object.entries(Details)) {
      if (!hasValidCoordinates(detail)) continue;

      const isHorizontal = isHorizontalMeasurement(detail);

      const x1Norm = detail.Points.Point1.X / imageDimensions.width;
      const x2Norm = detail.Points.Point2.X / imageDimensions.width;
      const y1Norm = detail.Points.Point1.Y / imageDimensions.height;
      const y2Norm = detail.Points.Point2.Y / imageDimensions.height;

      const x1Container = offsetX + x1Norm * displayedImageWidth;
      const x2Container = offsetX + x2Norm * displayedImageWidth;
      const y1Container = offsetY + y1Norm * displayedImageHeight;
      const y2Container = offsetY + y2Norm * displayedImageHeight;

      const x1Percent = (x1Container / containerWidth) * 100;
      const x2Percent = (x2Container / containerWidth) * 100;
      const y1Percent = (y1Container / containerHeight) * 100;
      const y2Percent = (y2Container / containerHeight) * 100;

      if (isHorizontal) {
        lines.push(
          <div
            key={key}
            className="absolute z-10 border-t-2 border-white border-dotted"
            style={{
              top: `${y1Percent}%`,
              left: `${x1Percent}%`,
              width: `${Math.abs(x2Percent - x1Percent)}%`,
              height: "0px",
            }}
          >
            <div className="absolute w-1.5 h-1.5 bg-white rounded-full -left-0.5 -top-1"></div>
            <div className="absolute w-1.5 h-1.5 bg-white rounded-full -right-0.5 -top-1"></div>
          </div>
        );
      } else {
        lines.push(
          <div
            key={key}
            className="absolute z-10 border-l-2 border-white border-dotted"
            style={{
              left: `${x1Percent}%`,
              top: `${y1Percent}%`,
              height: `${Math.abs(y2Percent - y1Percent)}%`,
              width: "0px",
            }}
          >
            <div className="absolute w-1.5 h-1.5 bg-white rounded-full -left-1 -top-0.5"></div>
            <div className="absolute w-1.5 h-1.5 bg-white rounded-full -left-1 -bottom-0.5"></div>
          </div>
        );
      }
    }

    return <div className="absolute inset-0 pointer-events-none">{lines}</div>;
  };

  return (
    <div className="flex items-center justify-center w-fit" ref={containerRef}>
      <div className="relative grid place-content-center w-[15rem] h-[25rem] bg-white border border-gorgonzola-blue-100 rounded-md overflow-hidden">
        <img src={`data:image/png;base64,${imageBase64}`} alt={`Uploaded garment ${safeIndex + 1}`} className="object-contain w-[15rem] h-[25rem]" onLoad={handleImageLoad} />
        {getMeasurementLines()}
      </div>
    </div>
  );
};

export default InteractiveGarmentMeasurementResult;
